import React, { useState, useEffect, useCallback, useRef } from 'react'

import { Text } from '@chakra-ui/react'
import { useNavigate } from 'react-router'
import { selectTrialHospitalAndReload } from 'src/features/auth/api/selectTrialHospital/selectTrialHospitalAndReload'
import { Role } from 'src/legacy_modules/entities/member/entity'
import {
  Hospital,
  TrialSelection,
  ScreenPhase,
} from 'src/legacy_modules/entities/select-trial/entity'
import { routes } from 'src/legacy_modules/routes'
import styled, { keyframes } from 'styled-components'

import { blue, gray } from './color/palette'
import { Message } from './message/message'
import { Modal } from './modal/modal'
import { ModalContent } from './modal/modal-content'
import { ModalTitle } from './modal/modal-title'

type Props = {
  trialSelections: TrialSelection[]
}

export const SelectTrialModal: React.FC<Props> = props => {
  const { trialSelections } = props

  const [selectedTrial, setSelectedTrial] = useState<TrialSelection>()
  const [hospitals, setHospitals] = useState<Hospital[]>([])
  const [phase, setPhase] = useState(ScreenPhase.Trial)
  const [errorMessage, setErrorMessage] = useState('')

  /**
   * [NOTE]
   * StrictModeでの動作時、useEffectの発火タイミングによってwindow.location.hrefの値が変わり意図しないルーティングが発生していたため
   * 初回レンダリング時の値を保持するためuseRefを使用する
   */
  const initialPathRef = useRef(window.location.href)

  const navigate = useNavigate()

  const confirmSelection = useCallback(
    async (trialSelection: TrialSelection, trialHospitalUid?: string) => {
      try {
        await selectTrialHospitalAndReload({
          trialUid: trialSelection.uid,
          trialHospitalUid: trialHospitalUid,
          path: initialPathRef.current,
        })
        navigate('/switch')
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : String(error))
        if (
          (error as { original?: { response?: { status?: number } } }).original
            ?.response?.status === 403
        ) {
          navigate(routes.httpError403)
        }
      }
    },
    [navigate],
  )

  const transitionLogic = useCallback(
    (selectedTrial: TrialSelection) => {
      if (selectedTrial.trialHospitals.length === 0) {
        setErrorMessage('試験・医療機関情報の取得に失敗しました。')
      }

      //ロールがDMの場合は医療機関を選択せずにダッシュボードに遷移
      if (selectedTrial.role === Role.DM) {
        confirmSelection(selectedTrial)
        return
      }

      //関連する医療施設が１つだけの場合はダッシュボードに遷移
      if (selectedTrial.trialHospitals.length === 1) {
        const singleHospitalUid = selectedTrial.trialHospitals[0].uid
        confirmSelection(selectedTrial, singleHospitalUid)
        return
      }

      setPhase(ScreenPhase.Hospital)
    },
    [confirmSelection],
  )

  useEffect(() => {
    if (trialSelections.length === 0) {
      setErrorMessage('試験・医療機関情報の取得に失敗しました。')
    }
    if (trialSelections.length === 1) {
      //単一の治験にしか紐づいていない場合は治験選択画面を省略
      const singleTrial = trialSelections[0]
      setHospitals(singleTrial.trialHospitals)
      setSelectedTrial(singleTrial)
      transitionLogic(singleTrial)
    }
  }, [transitionLogic, trialSelections])

  const onSelectTrial = (selectedTrialUid: string) => {
    const selectedTrial = getSingleTrial(selectedTrialUid)

    if (selectedTrial) {
      setHospitals(selectedTrial.trialHospitals)
      setSelectedTrial(selectedTrial)
      transitionLogic(selectedTrial)
    } else {
      setErrorMessage('試験情報の取得に失敗しました。')
    }
  }

  const onSelectHospital = (selectedHospitalUid: string) => {
    if (selectedTrial) {
      confirmSelection(selectedTrial, selectedHospitalUid)
    } else {
      setErrorMessage('試験の選択に失敗しました。操作をやり直してください。')
    }
  }

  const getSingleTrial = (selectedTrialUid: string) => {
    return trialSelections.find(item => {
      return item.uid === selectedTrialUid
    })
  }

  const titleLabel =
    phase === 0 ? '臨床試験を選んでください' : '医療機関を選んでください'

  return trialSelections.length === 1 &&
    (trialSelections[0].trialHospitals.length === 1 ||
      trialSelections[0].role === Role.DM) ? (
    <></>
  ) : (
    <>
      {!!errorMessage ? (
        <Message type="error" message={errorMessage} centered />
      ) : (
        <Modal onClose={() => {}} closable={false}>
          <ModalTitle title={titleLabel} />
          <ModalContent>
            <Content>
              {phase === ScreenPhase.Trial && (
                <>
                  {trialSelections.map(item => {
                    return (
                      <Select
                        key={item.uid}
                        onClick={() => onSelectTrial(item.uid)}
                      >
                        <Text>{item.name}</Text>
                      </Select>
                    )
                  })}
                </>
              )}
              {phase === ScreenPhase.Hospital && (
                <>
                  {hospitals.map(item => {
                    return (
                      <SlideInSelect
                        key={item.uid}
                        onClick={() => onSelectHospital(item.uid)}
                      >
                        <Text>{item.name}</Text>
                      </SlideInSelect>
                    )
                  })}
                </>
              )}
            </Content>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

const slidein = keyframes`
  0% {
    opacity: 0;
    transform: translateX(300px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`

const Content = styled.div`
  width: 100%;
  max-height: 200px;
  box-sizing: border-box;
  overflow-y: auto;
`

const Select = styled.div`
  display: flex;
  padding: 14px;
  box-sizing: border-box;
  border-bottom: 1px dashed ${gray[40]};
  cursor: pointer;
  word-break: break-word;
  &:hover {
    background-color: ${blue[10]};
  }
`

const SlideInSelect = styled(Select)`
  animation: ${slidein} 0.8s;
`
